import {
  ColumnDef,
  GroupingState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/table";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

type TableClassNames = {
  containerDiv?: string;
  table?: string;
  header?: string;
  head?: string;
  body?: string;
  row?: string;
  cell?: string;
};
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  classNames?: TableClassNames;
  // Optional initial grouping (e.g. ["campaign"])
  initialGrouping?: string[];
}

export function DataTable<TData, TValue>({
  classNames = {} as TableClassNames,
  columns,
  data,
  initialGrouping = [],
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [grouping, setGrouping] = useState<GroupingState>(initialGrouping as GroupingState);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const table = useReactTable({
    data,
    columns,
    // Support nested rows provided via `subRows` on each original row.
    getSubRows: (originalRow: any) => originalRow?.subRows ?? [],
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onGroupingChange: setGrouping,
    onExpandedChange: setExpanded,
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      grouping,
      expanded,
    },
  });

  const groupRowIdsKey = table
    .getGroupedRowModel()
    .rows.map(r => r.id)
    .join(",");

  useEffect(() => {
    const groupRows = table.getGroupedRowModel().rows;
    if (!groupRows || !groupRows.length) return;
    const expandedMap: Record<string, boolean> = {};
    for (const r of groupRows) expandedMap[r.id] = true;
    setExpanded(expandedMap);
  }, [groupRowIdsKey]);

  return (
    <div className={cn("rounded-md border mb-4 ", classNames.containerDiv || "")}>
      <Table className={classNames.table || ""}>
        <TableHeader className={classNames.header || "bg-muted rounded-md"}>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id} className="rounded-md">
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      "py-3",
                      classNames.head || "",
                      `${index === 0 && "rounded-tl-md"}`,
                      `${index === headerGroup.headers.length - 1 && "rounded-tr-md"}`,
                    )}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className={classNames.body || ""}>
          {table.getRowModel().rows?.length ? (
            (() => {
              const rows = table.getRowModel().rows;
              return rows.flatMap((row, idx) => {
                const primaryRow = (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={classNames.row || ""}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell className={classNames.cell || ""} key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );

                if (row.getIsGrouped && row.getIsGrouped() && row.subRows && row.subRows.length) {
                  const next = rows[idx + 1];
                  const coreHasChildrenHere = next && next.depth > row.depth;
                  if (coreHasChildrenHere) {
                    return [primaryRow];
                  }
                  const childRows = row.subRows.map(sub => (
                    <TableRow
                      key={sub.id}
                      data-state={sub.getIsSelected() && "selected"}
                      className={classNames.row || ""}
                    >
                      {sub.getVisibleCells().map(cell => (
                        <TableCell className={classNames.cell || ""} key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ));
                  return [primaryRow, ...childRows];
                }
                return [primaryRow];
              });
            })()
          ) : (
            <TableRow className={classNames.row || ""}>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
